<template>
  <div class="data-table">
    <!--
      Table Action Slots
    -->
    <div class="d-flex align-items-center justify-content-between">
      <div class="actions d-flex align-items-center">
        <!--
          Action Dropdown
        -->
        <div class="action-wrap">
          <b-dropdown
            class="filter-dropdown action-dropdown"
            @active-change="onDropdownOpen"
            :close-on-click="false"
            ref="action"
            trap-focus
          >
            <button class="close" @click="closeFilterDropdown('action', true)"><i class="icon-cross2"></i></button>

            <h4 class="header">{{ 'Sütunlar'|trans }}</h4>
            <draggable class="content" v-model="columns" v-bind="dragOptions" :forceFallback="true" @end="onColumnDrag">
              <transition-group type="transition" tag="div">
                <div
                  class="fullwidth d-flex align-center item"
                  v-for="column in columns"
                  :key="column.field"
                  v-if="column.field !== 'action'"
                >
                  <i class="fas fa-bars move"></i>
                  <div class="custom-control custom-control-sm custom-checkbox">
                    <input
                      type="checkbox"
                      :id="'c' + column.field"
                      class="custom-control-input"
                      v-model="column.visible"
                      @change="onColumnHide"
                    >
                    <label class="custom-control-label" :for="'c' + column.field">{{ column.name }}</label>
                  </div>
                </div>
              </transition-group>
            </draggable>
          </b-dropdown>
        </div>

        <div v-if="!filtersOnBottom">
          <button
            :class="{'disabled' : getColumnFilters.length < 1}"
            @click.prevent="clearColumnFilter(columns, true); onFilter()"
            class="btn btn-sm btn-warning btn-labeled btn-labeled-right mb-3"
          >
            {{ "Filtreleri Temizle" | trans }}
            <b class="d-flex"><i class="fas fa-times"></i></b>
          </button>

          <!--"actions" Slot-->
          <slot name="actions" :data="checkedRows" :action="action"></slot>
        </div>
      </div>

      <!--"custom-actions" Slot-->
      <div class="customs ml-auto">
        <slot name="custom-actions" :action="action"></slot>
      </div>
    </div>

    <!--
      Data Table
    -->
    <b-table
      :data="data"
      :checked-rows.sync="checkedRows"
      :checkable="checkable"
      :striped="striped"
      :hoverable="hoverable"
      :scrollable="scrollable"
      :loading="isLoading"
      :sticky-header="stickyHeader"
      :narrowed="false"
      :bordered="false"
      :mobile-cards="false"
      :is-row-selectable="() => false"
      backend-pagination
      :total="pager.total"
      :per-page="pager.perPage"
      :current-page.sync="pager.page"
    >
      <!--
        Action Column
      -->
      <b-table-column :visible="true" key="action" width="10" cell-class="action" header-class="action">
        <template v-slot:header="{ column }">
          <div class="filter-dropdown">
            <a @click="toggleActionDropdown" v-tooltip :title="'Kolon Ekle/Çıkar'|trans"><i class="icon-plus2"></i></a>
          </div>
        </template>

        <!-- Column Data -->
        <template v-slot="props">
          <div class="actions-wrap list-icons">
            <b-dropdown append-to-body trap-focus class="actions-wrap" @active-change="onDropdownOpen" :ref="'actionDropdown' + props.row._item.id">
              <a slot="trigger"><i class="icon-menu"></i></a>
              <!-- "Action" slot -->
              <slot name="action" :data="props" :action="action" :item="props.row._item"></slot>
            </b-dropdown>
          </div>
        </template>
      </b-table-column>

      <!--
        Table Data & Columns
      -->
      <b-table-column
        v-for="column in columns"
        :key="column.id"
        :field="column.field"
        :label="column.name"
        :visible="column.visible"
        :width="column.width"
        :centered="column.centered"
        :sortable="false"
        :searchable="false"
        :meta="column"
        header-class="align-middle"
      >
        <template v-slot:header="{ column }">
          <div class="d-flex align-items-center">
            <!--Sortable Header-->
            <div
              class="pointer sortable d-flex align-items-center"
              v-tooltip
              :title="'Sırala' | trans"
              v-if="column.meta.sortable"
              @click="onSort(column.meta.field)"
            >
              <span>{{ column.label }}</span>
              <i class="fas fa-sort-amount-up ml-1" v-if="(pager.sortOrder === 'asc') && (pager.sortField === column.field)"></i>
              <i class="fas fa-sort-amount-down ml-1" v-else-if="pager.sortField === column.field"></i>
            </div>
            <span v-else>{{ column.label }}</span>

            <!--Searchable Header-->
            <b-dropdown
              class="filter-dropdown"
              append-to-body
              v-if="column.meta.filter"
              v-on:active-change="onFilterDropdownChange($event, column.meta.field); onDropdownOpen($event)"
              :ref="column.meta.field"
            >
              <a slot="trigger" :class="{'text-primary': !column.meta.filterEmpty}" class="trigger">
                <i class="fas fa-search" v-if="column.meta.filterEmpty"></i>
                <i class="fas fa-search-plus" v-else></i>
              </a>

              <button class="close" @click="closeFilterDropdown(column.meta.field, true)"><i class="icon-cross2"></i></button>
              <div class="filter-content">
                <template v-for="filter in column.meta.filter">
                  <!--Text-->
                  <textinput
                    :filter="filter"
                    v-if="filter.type === 'text'"
                    v-model="filter.data"
                    v-on:keydown="onFilter(column.meta); closeFilterDropdown(column.meta.field, true);"
                  ></textinput>

                  <!--Number-->
                  <textinput
                    :filter="filter"
                    v-if="filter.type === 'number'"
                    v-model="filter.data"
                    v-on:keydown="onFilter(column.meta, true); closeFilterDropdown(column.meta.field, true);"
                  ></textinput>

                  <!--Switch-->
                  <div class="custom-control custom-switch" v-else-if="filter.type === 'switch'">
                    <input
                      type="checkbox"
                      :id="filter.field"
                      class="custom-control-input"
                      :checked="filter.default || filter.data"
                      @change="filter.data = Boolean($event.target.checked)"
                    >
                    <label class="custom-control-label" :for="filter.field">{{ filter.placeholder || 'Seçiniz..' | trans }}</label>
                  </div>

                  <!--Checkbox-->
                  <div class="custom-control custom-checkbox" v-else-if="filter.type === 'checkbox'">
                    <input
                      type="checkbox"
                      :id="filter.field"
                      class="custom-control-input"
                      :checked="filter.default || filter.data"
                      @change="filter.data = Boolean($event.target.checked)"
                    >
                    <label class="custom-control-label" :for="filter.field">{{ filter.placeholder || 'Seçiniz..' | trans }}</label>
                  </div>

                  <!--Select-->
                  <div class="form-control-wrap" v-else-if="filter.type === 'select'">
                    <select2 :filter="filter" v-model="filter.data"></select2>
                  </div>

                  <!--DatePicker-->
                  <datepicker
                    v-else-if="filter.type === 'date'"
                    :range="filter.range"
                    :placeholder="filter.placeholder || 'Seçiniz..' | trans"
                    :value="filter.data"
                    v-model="filter.data"
                  ></datepicker>

                  <!--TimePicker-->
                  <timepicker
                    v-else-if="filter.type === 'time'"
                    :value="filter.data"
                    :placeholder="filter.placeholder || 'Seçiniz..' | trans"
                    v-model="filter.data"
                  ></timepicker>
                </template>
              </div>

              <div class="filter-content mt-2">
                <div class="filter-action d-flex align-center justify-content-end">
                  <a
                    href="#!"
                    class="btn btn-sm btn-danger keep"
                    tabindex="1"
                    @click.prevent="clearColumnFilter(column.meta, true); onFilter(); closeFilterDropdown(column.meta.field, true);"
                    :class="{'disabled': column.meta.filterEmpty}"
                  ><i class="fas fa-trash"></i></a>
                  <a
                    href="#!"
                    class="btn btn-sm btn-primary ml-1"
                    @click.prevent="onFilter(column.meta); closeFilterDropdown(column.meta.field, true);"
                  ><i class="fas fa-search mr-1"></i><span>{{ "Filtrele" | trans }}</span></a>
                </div>
              </div>
            </b-dropdown>
          </div>
        </template>

        <!-- Column Data -->
        <template v-slot="props">
          <template v-if="column.template">
            <component :is="{ template: column.template, data () { return { data: props.row } } }"/>
          </template>
          <template v-else><div v-html="props.row[column.name]" /></template>
        </template>
      </b-table-column>

      <!--
        Footer
      -->
      <template slot="footer" v-if="autoTotal">
        <th class="is-hidden-mobile" v-if="checkable">
          <div class="th-wrap"></div>
        </th>
        <th class="is-hidden-mobile">
          <div class="th-wrap"></div>
        </th>
        <th
          class="is-hidden-mobile"
          v-for="column in columns"
          v-if="column.visible"
          :style="{'width': column.width, 'text-align': column.centered}"
        >
          <template v-if="column.field === 'id' || column.countable === false"></template>
          <template v-else>
            <template v-if="column.templateTotal">
              <component
                :is="{
                        template: column.templateTotal,
                        data() { return { column, data, total: calculateColumnTotal(column.field) } }
                      }"
              />
            </template>
            <div class="th-wrap" v-else>{{ calculateColumnTotal(column.field) }}</div>
          </template>
        </th>
      </template>

      <!--
        Empty Template
      -->
      <template slot="empty">
        <section class="section">
          <div class="content has-text-grey has-text-centered p-2">
            <p><i class="icon-folder-search mr-3 icon-2x"></i></p>
            <p class="mb-0">{{ "Kayıt bulunmuyor." | trans }}</p>
          </div>
        </section>
      </template>
    </b-table>

    <!--
      Pagination
    -->
    <div class="paginator d-flex align-items-center flex-column flex-sm-row justify-content-sm-between">
      <div v-if="filtersOnBottom" class="bottom-filters">
        <button
          :class="{'disabled' : getColumnFilters.length < 1}"
          @click.prevent="clearColumnFilter(columns, true); onFilter()"
          class="btn btn-sm btn-warning btn-labeled btn-labeled-right"
        >
          {{ "Filtreleri Temizle" | trans }}
          <b class="d-flex"><i class="fas fa-times"></i></b>
        </button>

        <!--"actions" Slot-->
        <slot name="actions" :data="checkedRows" :action="action"></slot>
      </div>

      <div class="d-flex align-items-center" :class="{'btn-block': !filtersOnBottom}">
        <div class="total pr-4 mr-auto">
          {{ "Toplam" | trans }}: <b>{{ getCurrentPagerCount }}/{{ pager.total }}</b>
        </div>
        <b-pagination
          :total="pager.total"
          :current.sync="pager.page"
          :per-page="pager.perPage"
          range-before="1"
          range-after="1"
          order="is-centered"
          :simple="false"
          :rounded="false"
          @change="onPageChange"
        >
          <b-pagination-button slot="previous" slot-scope="props" :page="props.page" tag="a">
            <i class="fas fa-arrow-left"></i>
          </b-pagination-button>
          <b-pagination-button slot="next" slot-scope="props" :page="props.page" tag="a">
            <i class="fas fa-arrow-right"></i>
          </b-pagination-button>
        </b-pagination>
      </div>
    </div>
  </div>
</template>

<script>
  import Vue from 'vue'

  /* Draggable */
  import Draggable from "vuedraggable";

  /* Buefy */
  // noinspection ES6CheckImport
  import { Checkbox, Dialog, Dropdown, Pagination, Table } from "buefy";

  /* Libs & Bootstrap Wrapper */
  import Storage from "./Storage";
  import Textinput from "../Wrapper/Textinput";
  import Tooltip from "../Wrapper/Tooltip";
  import Select2 from "../Wrapper/Select2";
  import Datepicker from "../Wrapper/Datepicker";
  import Timepicker from "../Wrapper/Timepicker";

  Vue.component('draggable', Draggable);

  [Table, Pagination, Dialog, Checkbox, Dropdown].forEach((x) => Vue.use(x));

  export default {
    components: {Select2, Datepicker, Timepicker, Textinput, Tooltip},
    props: {
      tableName: {type: String, default: 'default'},
      tableApi: {type: String, default: ''},
      columnApi: {type: String, default: '/user/table-settings'},
      columnList: {type: Array, default: () => []},
      defaultOrder: {type: String, default: 'id, desc'},
      columnVisible: {type: Boolean, default: true},
      checkable: {type: Boolean, default: false},
      stickyHeader: {type: Boolean, default: true},
      striped: {type: Boolean, default: false},
      hoverable: {type: Boolean, default: false},
      scrollable: {type: Boolean, default: false},
      scrollTop: {type: Boolean, default: true},
      autoTotal: {type: Boolean, default: false},
      filtersOnBottom: {type: Boolean, default: true},
    },

    data() {
      this.dragOptions = {
        animation: 250,
        group: "description",
        disabled: false,
        ghostClass: "ghost"
      }

      return {
        csrf: null,
        isActionActive: false,
        isLoading: false,
        pager: {
          sortField: '',
          sortOrder: '',
          page: 1,
          perPage: 40,
          total: 0
        },
        checkedRows: [],
        data: [],
        columns: []
      }
    },

    computed: {
      getCurrentPagerCount() {
        return this.pager.page * this.pager.perPage > this.pager.total ? this.pager.total : this.pager.page * this.pager.perPage;
      },
      getHiddenColumn() {
        return this.columns
          .filter((item) => item.visible === false)
          .map((item) => item.field);
      },
      getOrderColumn() {
        return this.columns.map((item) => {
          return item.field;
        });
      },
      /**
       * Get Column Filters URL Format
       */
      getColumnFilters() {
        let filters = [];

        this.columns.forEach((column) => {
          if (column.hasOwnProperty('filter')) {
            column.filter.forEach((filter) => {
              if (filter.data instanceof Object) {
                if (filter.data instanceof Array) {
                  filter.data.forEach((value) => {
                    filters.push(`filters[${filter.field}][]=${value}`);
                    column.filterEmpty = false;
                  });
                } else {
                  Object.keys(filter.data).map((key) => {
                    if (filter.data[key] !== null) {
                      filters.push(`filters[${filter.field}][${key}]=${filter.data[key]}`);
                      column.filterEmpty = false;
                    }
                  })
                }
              } else if (filter.hasOwnProperty('data') && (filter.data !== null)) {
                filters.push(`filters[${filter.field}]=${filter.data}`);
                column.filterEmpty = false;
              }
            })
          }
        });

        return filters;
      },
    },

    methods: {
      /**
       * Load Data for Backend
       */
      loadAsyncData(reloadPagination, onSuccess) {
        // Start Loading
        this.isLoading = true;

        // Load Data
        $.ajax({
          type: "GET",
          dataType: "json",
          cache: false,
          url: `${this.tableApi}?${this.prepareQuery()}`,
          success: (response) => {
            // Set Data
            this.data = response.data;

            // Pagination
            if (reloadPagination) {
              this.pager.total = response.meta.total;
              this.pager.perPage = response.meta.per_page;
            }

            // Change URL
            this.changeHrefSilent();

            if (typeof onSuccess !== 'undefined') {
              onSuccess();
            }
          },
          complete: () => {
            this.isLoading = false;
          }
        });
      },

      /**
       * Pagination Page Change Trigger
       *
       * @param page
       */
      onPageChange(page) {
        this.pager.page = page
        this.loadAsyncData(false, () => {
          if (this.scrollTop) {
            let title = $(".b-table");
            if (title.length > 0) {
              $('html,body').animate({
                scrollTop: title.offset().top - 6
              }, 350)
            }
          }
        });
      },

      /**
       * Column Sortable Trigger
       *
       * @param field
       * @param order
       */
      onSort(field, order) {
        if (this.pager.sortField === field) {
          this.pager.sortOrder = order ?? (this.pager.sortOrder === 'desc' ? 'asc' : 'desc');
        } else {
          this.pager.sortField = field
        }

        this.loadAsyncData();
      },

      /**
       * Column Filter Trigger
       */
      onFilter(clearEmptyColumnFilter) {
        if (clearEmptyColumnFilter) {
          this.clearColumnFilter(clearEmptyColumnFilter, true, true)
        }

        this.loadAsyncData(true);
      },

      /**
       * Column Show/Hide Trigger
       *
       * @param field
       * @param status
       */
      onColumnHide(field, status) {
        let url = `${this.columnApi}?table_name=${this.tableName}`;

        // Send Backend
        $.ajax({
          type: "POST",
          dataType: "json",
          url: url,
          data: {
            'hidden': this.getHiddenColumn,
            'order': this.getOrderColumn,
            '_token': this.csrf,
          },
          success: () => {
            // Clear Local Storage
            Storage.removeStorage(url);
          }
        });
      },

      /**
       * Column Drag End Trigger
       *
       * @param event
       */
      onColumnDrag(event) {
        this.onColumnHide(null, null);
      },

      /**
       * Filter Dropdown Show/Hide Event
       */
      onFilterDropdownChange(status, columnId) {
        if (status) {
          this.closeFilterDropdown(columnId);
          this.focusFilterInput(columnId);
        }
      },

      /**
       * Prepare AJAX Query
       */
      prepareQuery(withOutputParam = true) {
        const result = [
          `orderBy=${this.pager.sortField},${this.pager.sortOrder}`,
          `page=${this.pager.page}`,
          ...this.getColumnFilters
        ];

        if (withOutputParam) {
          result.push('output=table');
        }

        return result.join('&');
      },

      // /**
      //  * Prepare AJAX Query
      //  */
      // prepareQuery() {
      //   const params = {
      //     output: 'table',
      //     orderBy: `${this.pager.sortField},${this.pager.sortOrder}`,
      //     page: this.pager.page,
      //     ...this.getColumnFilters
      //   };

      //   return $.param(params);
      // },

      /**
       * Set Pagination Data
       */
      setOrderByData(queryString) {
        let orderBy = queryString.split(',');
        if (orderBy[0]) {
          this.pager.sortField = orderBy[0];
        }
        if (orderBy[1]) {
          this.pager.sortOrder = orderBy[1]
        }
      },

      /**
       * Parse URL to Data
       */
      setQueryStringToData() {
        let uri = window.location.search.substring(1);
        let searchParams = new URLSearchParams(decodeURI(uri));

        // Set Pagination Data
        if (searchParams.has('orderBy')) {
          this.setOrderByData(searchParams.get('orderBy'))
        }
        if (searchParams.has('page')) {
          this.pager.page = parseInt(searchParams.get('page'))
        }

        // Set Columns Filter Data
        this.columns
          .filter((column) => {
            return column.hasOwnProperty('filter')
          })
          .map((column) => {
            column.filter.forEach((filter) => {
              if (filter.hasOwnProperty('range') && filter.range) {
                let data = {};

                if (searchParams.has(`filters[${filter.field}][start]`)) {
                  data.start = searchParams.get(`filters[${filter.field}][start]`)
                }
                if (searchParams.has(`filters[${filter.field}][end]`)) {
                  data.end = searchParams.get(`filters[${filter.field}][end]`)
                }
                if (Object.keys(data).length > 0) {
                  filter.data = data;
                  column.filterEmpty = false;
                }

                return;
              }

              if (filter.hasOwnProperty('multiple') && filter.multiple) {
                let params = searchParams.getAll(`filters[${filter.field}][]`);
                filter.data = params.length ? params : null;
              }

              if (searchParams.has(`filters[${filter.field}]`)) {
                switch (filter.type) {
                  case 'number':
                    filter.data = parseInt(searchParams.get(`filters[${filter.field}]`));
                    break;
                  case 'switch':
                    filter.data = searchParams.get(`filters[${filter.field}]`).toLowerCase() === 'true';
                    break;
                  case 'checkbox':
                    filter.data = searchParams.get(`filters[${filter.field}]`).toLowerCase() === 'true';
                    break;
                  default:
                    filter.data = searchParams.get(`filters[${filter.field}]`);
                }
              }

              // Set Filter Empty
              if (filter.data) {
                column.filterEmpty = false;
              }
            })
          })
      },

      /**
       * Order Array for Other Array
       */
      mapOrder(array, order, key) {
        if (order.length) {
          array.sort(function (a, b) {
            let A = a[key],
              B = b[key];

            if (order.indexOf(A) > order.indexOf(B) || order.indexOf(A) === -1 || order.indexOf(B) === -1) {
              return 1;
            } else {
              return -1;
            }
          });
        }

        return array;
      },

      /**
       * Load Table Hidden Column & Column Order
       */
      loadColumnVisibleOrder(localStore) {
        if (this.columnVisible) {
          let url = `${this.columnApi}?table_name=${this.tableName}`;
          let process = (response, save) => {
            // Hide
            this.columns.forEach((item) => {
              if (response.hidden && response.hidden.indexOf(item.field) !== -1) {
                item.visible = false;
              }
            });

            // Order
            this.mapOrder(this.columns, response.order ?? [], 'field')

            // Save Response to Local Storage
            if (localStore && save) {
              Storage.setStorage(url, response, 3600);
            }
          };

          // Using Local Storage
          if (localStore) {
            let response = Storage.getStorage(url);
            if (response !== null) {
              return process(response, false)
            }
          }

          $.get(url, (response) => {
            process(response, true)
          });
        }
      },

      /**
       * Slot Actions
       */
      action() {
        let v = this;

        return {
          link (path, params) {
            for (const [key, value] of Object.entries(params)) {
              path = path.replace(key, value);
            }

            return path;
          },

          remove (path, param, index, onSuccess) {
            let link = this.link(path, param);

            v.$buefy.dialog.confirm({
              title: v.trans('Sil'),
              message: v.trans('Bu kaydı silmek istediğinize emin misiniz?'),
              confirmText: v.trans('Sil'),
              cancelText: v.trans('İptal'),
              type: 'is-danger',
              hasIcon: true,
              onConfirm: () => {
                v.isLoading = true;

                // Send Backend
                $.ajax({
                  url: link,
                  type: 'POST',
                  dataType: 'json',
                  data: {
                    '_token': v.csrf
                  },
                  success: (response) => {
                    Lagina.notifySuccess(response.message);

                    if (typeof onSuccess !== 'undefined') {
                      onSuccess();
                    } else {
                      v.$delete(v.data, index);
                    }
                  },
                  error: (response) => {
                    Lagina.notifyError(response.statusText);
                  },
                  complete: () => {
                    v.isLoading = false;
                  }
                });
              }
            });
          }, // end remove()

          removeRefresh(path, param, index) {
            this.remove(path, param, index, () => {
              v.loadAsyncData(true)
            });
          },
        } // end return
      }, // end action()

      /**
       * Change Page Query String
       */
      changeHrefSilent() {
        let path = window.location.origin + window.location.pathname + '?' + this.prepareQuery(false);
        history.replaceState({href: path}, null, path);
      },

      /**
       * Select 2 Prepare Data
       */
      select2PrepareData(object) {
        let option = [];
        for (const [key, value] of Object.entries(object)) {
          option.push({
            id: key,
            text: value
          });
        }
        return option;
      },

      /**
       * Clear Column Filter Data
       */
      clearColumnFilter(columns, resetPage, onlyEmpty) {
        if (typeof columns.length === 'undefined') {
          columns = [columns];
        }

        if (resetPage) {
          this.pager.page = 1;
        }

        columns.forEach((item) => {
          if (item.hasOwnProperty('filter')) {
            let allEmpty = true;

            item.filter.forEach((filter) => {
              if (filter.data instanceof Object) {
                if (filter.data instanceof Array) {
                  if ((onlyEmpty && filter.data.length === 0) || !onlyEmpty) {
                    filter.data = null;
                  } else {
                    allEmpty = false;
                  }
                } else {
                  Object.keys(filter.data).map((key) => {
                    if (onlyEmpty) {
                      if (!filter.data[key]) {
                        filter.data[key] = null;
                      } else {
                        allEmpty = false;
                      }
                    } else {
                      filter.data[key] = null;
                    }
                  })
                }
              } else {
                if (onlyEmpty) {
                  if (filter.data === '') {
                    filter.data = null;
                  } else {
                    allEmpty = false;
                  }
                } else {
                  filter.data = null;
                }
              }
            })

            if (allEmpty) {
              item.filterEmpty = true;
            }
          }
        });
      },

      /**
       * Focus Filter Input|Textarea|Select
       */
      focusFilterInput(columnId) {
        let el = this.$refs[columnId][0].$refs.dropdownMenu;
        let input = el.querySelector('input'),
          select = el.querySelector('select');

        setTimeout(() => {
          if (input && !input.hasAttribute('data-datepicker') && !input.hasAttribute('data-timepicker')) {
            $(input).focus();
          }

          if (select) {
            $(select).parent().find('.select2-selection').focus()
          }
        }, 25);
      },

      /**
       * Close Filter Dropdown
       */
      closeFilterDropdown(columnId, selfClose) {
        if (selfClose) {
          let dropdown = this.$refs[columnId][0] ?? this.$refs[columnId]
          if (dropdown.isActive) {
            dropdown.toggle()
          }
        } else {
          for (const [key, dropdown] of Object.entries(this.$refs)) {
            if (key !== columnId && dropdown instanceof Array) {
              if (dropdown[0].hasOwnProperty('toggle') && dropdown[0].isActive) {
                dropdown[0].toggle()
              }
            }
          }
        }
      },

      /**
       * Calculate Auto Height for Sticky Header
       */
      setAutoHeight() {
        if (this.stickyHeader) {
          setTimeout(() => {
            let table = $(this.$el).find('.has-sticky-header');
            let afterElHeight = $(this.$el).find('.paginator').height();

            table.css('max-height', `calc(100vh - ${(table.position().top + 2) + (afterElHeight * 2)}px)`)
          }, 150);
        }
      },

      /**
       * Calculate Column Total
       * @param columnId
       * @param defaultVal
       * @returns {number|*}
       */
      calculateColumnTotal(columnId, defaultVal = '') {
        let data = this.data.map((row) => {
          return !isNaN(row[columnId]) ? parseFloat(row[columnId]) : 0;
        });

        if (data.length) {
          return data.reduce((a, b) => {
            return a + b;
          }) || defaultVal
        }

        return defaultVal;
      },

      /**
       * Toggle Action Dropdown
       */
      toggleActionDropdown() {
        this.$refs['action'].toggle();
      },

      /**
       * Dropdown Open Event
       */
      onDropdownOpen(status) {
        if (status) {
          $('body').addClass('dropdown-open')
        } else {
          $('body').removeClass('dropdown-open')
        }
      }
    },

    created() {
      // Prop Column to Data Column && Set Default Value
      this.columnList.forEach((item) => {
        if (!item.hasOwnProperty('visible')) {
          item.visible = true
        }

        if (item.hasOwnProperty('filter')) {
          item.filterEmpty = true
          item.filter.forEach((filter) => {
            if (!filter.hasOwnProperty('data')) {
              filter.data = null;
            }
            if (filter.hasOwnProperty('range') && filter.range) {
              filter.data = {
                start: null,
                end: null
              }
            }
          })
        }
      });
      this.columns = this.columnList;

      // Set Default Order
      if (this.defaultOrder) {
        this.setOrderByData(this.defaultOrder);
      }

      // Run Query String to Data Convertor
      this.setQueryStringToData()

      // Hide User Defined Columns
      this.loadColumnVisibleOrder(true);

      // Read Meta CSRF Token
      this.csrf = $('meta[name=csrf-token]').attr('content') ?? null;
    },

    mounted() {
      // Load First Data
      this.loadAsyncData(true, this.setAutoHeight());

      window.table = this;
    }
  }
</script>

<style lang="scss">
.bottom-filters {
  .btn {
    margin-bottom: 0 !important;
  }
}
</style>
